import { httpSingleton } from '@/lib/http-client';
import { constructUrl } from '@/lib/utils';
import { DocumentModel, PaginatedResponseModel, FormOption, LanguageOption } from '@/lib/models';

export interface GetDocumentSummaryResponseBody {
	audioSummaries: GetDocumentAudioResponseBody[];
	processing: boolean;
	summary: string | null;
}

export interface GetDocumentAudioResponseBody {
	audioUrl: string | null;
	createdDescription: string;
	language: string;
	languageCode: string;
	processing: boolean;
}

export const documentService = {
	getDocuments(queryParameters?: {
		bookmarked?: boolean;
		parentBusinessProcessIds?: string[];
		businessProcessIds?: string[];
		businessTaxonomyId?: string;
		documentTypes?: string[];
		fromDate?: string;
		link?: string;
		page?: string;
		size?: string;
		sortDirection?: string;
		sortField?: string;
		searchQuery?: string;
		toDate?: string;
		solutionIntelligenceIds?: string[];
		vendorIds?: string[];
		topicIds?: string[];
		includeDetails?: boolean;
		fromLastUpdateDate?: string;
		authorId?: string;
	}) {
		return httpSingleton.orchestrateRequest<PaginatedResponseModel & { documents: DocumentModel[] }>({
			method: 'GET',
			url: constructUrl('/advisory-service/api/v1/documents', queryParameters),
		});
	},
	getDocumentById(documentId: string) {
		return httpSingleton.orchestrateRequest<DocumentModel>({
			method: 'GET',
			url: `/advisory-service/api/v1/document/${documentId}`,
		});
	},
	getDocumentFilters(queryParameters?: {
		parentBusinessProcessIds?: string[];
		businessProcessIds?: string[];
		topicIds?: string[];
		solutionIntelligenceIds?: string[];
		vendorIds?: string[];
	}) {
		return httpSingleton.orchestrateRequest<{
			solutionIntelligence: FormOption[];
			vendors: FormOption[];
			businessProcesses: FormOption[];
			contentTypes: FormOption[];
			sortFields: FormOption[];
			topics: FormOption[];
		}>({
			method: 'GET',
			url: constructUrl('/advisory-service/api/v1/document-filter', queryParameters),
		});
	},
	trackDocumentDownload(documentId: string) {
		return httpSingleton.orchestrateRequest({
			method: 'GET',
			url: `/advisory-service/api/v1/document/${documentId}/download/activity`,
		});
	},
	trackDocumentView(documentId: string) {
		return httpSingleton.orchestrateRequest({
			method: 'GET',
			url: `/advisory-service/api/v1/document/${documentId}/view/activity`,
		});
	},

	getResearch(queryParameters?: {
		size?: number;
		page?: number;
		documentTypes?: string[];
		businessProcessIds?: string[];
		parentBusinessProcessIds?: string[];
		topicIds?: string[];
		solutionIntelligenceIds?: string[];
		vendorIds?: string[];
		bookmarked?: boolean;
		fromDate?: string;
		toDate?: string;
		searchQuery?: string;
		sortField?: string;
		sortDirection?: string;
		includeDetails?: boolean;
		fromLastUpdateDate?: string;
		authorId?: string;
	}) {
		return httpSingleton.orchestrateRequest<PaginatedResponseModel & { results: DocumentModel[] }>({
			method: 'GET',
			url: constructUrl('/advisory-service/api/v1/research/search', queryParameters),
		});
	},
	getResearchFilters(queryParameters?: {
		parentBusinessProcessIds?: string[];
		businessProcessIds?: string[];
		topicIds?: string[];
	}) {
		return httpSingleton.orchestrateRequest<{
			businessProcesses: FormOption[];
			contentTypes: FormOption[];
			sortFields: FormOption[];
			topics: FormOption[];
		}>({
			method: 'GET',
			url: constructUrl('/advisory-service/api/v1/research-filters', queryParameters),
		});
	},
	getDocumentLanguages() {
		return httpSingleton.orchestrateRequest<LanguageOption[]>({
			method: 'GET',
			url: `/advisory-service/api/v1/document/languages`,
		});
	},
	getDocumentSummary(documentId: string, data: { language: string }) {
		return httpSingleton.orchestrateRequest<GetDocumentSummaryResponseBody>({
			method: 'POST',
			url: `/advisory-service/api/v1/document/${documentId}/summary`,
			data,
		});
	},
	getDocumentAudio(documentId: string, data: { language: string }) {
		return httpSingleton.orchestrateRequest<GetDocumentAudioResponseBody>({
			method: 'POST',
			url: `/advisory-service/api/v1/document/${documentId}/audio`,
			data,
		});
	},
	downloadDocumentAudio(documentId: string, languageCode: string) {
		return httpSingleton.orchestrateRequest<Blob>({
			method: 'GET',
			url: `/advisory-service/api/v1/document/${documentId}/audio/${languageCode}/download`,
			responseType: 'blob',
		});
	},
};
